import { useState, useMemo, useRef, useEffect } from "react";
import { Search, Building2, ChevronRight, ChevronDown, LogOut } from "lucide-react";

const DistroList = ({ distros = [], handleClick, onSignOut }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showScrollHint, setShowScrollHint] = useState(false);
    const listRef = useRef(null);
    const showSearch = distros.length > 5;

    const filteredDistros = useMemo(() => {
        if (!searchQuery.trim()) return distros;
        const query = searchQuery.toLowerCase();
        return distros.filter(distro =>
            distro.name.toLowerCase().includes(query)
        );
    }, [distros, searchQuery]);

    // check if list is scrollable and update hint visibility
    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl) return;

        const checkScroll = () => {
            const isScrollable = listEl.scrollHeight > listEl.clientHeight;
            const isAtBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight < 20;
            setShowScrollHint(isScrollable && !isAtBottom);
        };

        checkScroll();
        listEl.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        return () => {
            listEl.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [filteredDistros.length]);

    const onSelect = (distroId) => {
        // create synthetic event matching existing handleClick signature
        handleClick({ target: { dataset: { id: distroId } } });
    };

    if (!distros || distros.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center w-full max-w-xl mx-auto bg-white p-8 shadow-card rounded-2xl my-16">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">No Firms Available</h4>
                <p className="text-sm text-gray-500">Please contact support if you believe this is an error.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col justify-center items-center w-full max-w-xl mx-auto bg-white p-8 shadow-card rounded-2xl my-16">
            <div className="w-full space-y-5">
                {/* header */}
                <div className="text-center">
                    <h4 className="text-2xl md:text-3xl font-bold text-gray-900">
                        Select Firm
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                        You have access to {distros.length} firm{distros.length !== 1 ? 's' : ''}
                    </p>
                </div>

                {/* search input */}
                {showSearch && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search firms..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all placeholder:text-gray-400"
                            autoFocus
                        />
                    </div>
                )}

                {/* distro list */}
                <div className="relative">
                    <div
                        ref={listRef}
                        className="space-y-2 max-h-96 overflow-y-auto scroll-smooth"
                    >
                        {filteredDistros.length > 0 ? (
                            filteredDistros.map((distro) => (
                                <button
                                    key={distro.id}
                                    onClick={() => onSelect(distro.id)}
                                    className="w-full flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-primary/5 hover:border-primary/30 transition-all duration-200 group"
                                >
                                    <div className="p-2 rounded-lg bg-white group-hover:bg-primary/10 transition-colors">
                                        <Building2 className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                                    </div>
                                    <span className="flex-1 text-left text-base font-medium text-gray-900 group-hover:text-primary transition-colors">
                                        {distro.name}
                                    </span>
                                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                                </button>
                            ))
                        ) : (
                            <p className="text-sm text-gray-500 text-center py-4">
                                No firms match your search
                            </p>
                        )}
                    </div>

                    {/* scroll hint - floating pill */}
                    <div
                        className={`absolute bottom-2 left-1/2 -translate-x-1/2 transition-all duration-300 ${
                            showScrollHint
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-2 pointer-events-none'
                        }`}
                    >
                        <button
                            onClick={() => listRef.current?.scrollBy({ top: 150, behavior: 'smooth' })}
                            className="flex items-center justify-center w-8 h-8 bg-white hover:bg-gray-50 border border-gray-200 rounded-full shadow-md transition-all duration-200 hover:scale-105 animate-bounce"
                            style={{ animationDuration: '2s' }}
                        >
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* sign out hint */}
                {onSignOut && (
                    <div className="pt-4 border-t border-gray-100 mt-2">
                        <button
                            onClick={onSignOut}
                            className="flex items-center justify-center gap-2 w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Sign out</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DistroList;
